import React from 'react'
import { inject, observer } from 'mobx-react'
import PointBuffer from 'stores/PointBuffer'
import Shape from 'stores/Shape'
import Line from 'stores/Line'
import Marker from 'stores/Marker'
import BoundaryList from 'stores/BoundaryList'
import UserShapefileList from 'stores/UserShapefileList'
import UserMarkerfileList from 'stores/UserMarkerfileList'
import { withTranslation } from 'react-i18next'
import Isochrone from '../../../stores/Isochrone'
import {
  bufferArountPoint,
  travelTime,
  drawShape,
  bufferArountLine,
  placeAMarker,
  placeAdministrativeBoundary,
  uploadedShapeFiles,
  uploadedMarkers,
  folderActions
} from 'constants.js'
import { getDefaultGrayColor } from '../../../utils'

const SvgIcon = ({ html, className = '' }) => (
  <span
    className={className ? `add-item-icon ${className}` : 'add-item-icon'}
    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    dangerouslySetInnerHTML={{ __html: html }}
  />
)

const AddItemPane = ({ uiStore, selectionStore, dataStore, t }) => {
  // Helper to select a new item without POI draft mode
  const selectNewItem = (item) => {
    const grayColor = getDefaultGrayColor(dataStore && dataStore.markerColors)
    if (grayColor && item && item.color_id == null) {
      item.color_id = grayColor.id
    }

    if (typeof uiStore.closeQuickCreate === 'function') {
      uiStore.closeQuickCreate()
    }
    if (typeof uiStore.closeQuickCreateModal === 'function') {
      uiStore.closeQuickCreateModal()
    }
    if (uiStore.map && uiStore.map.leafletElement && typeof uiStore.map.leafletElement.closePopup === 'function') {
      uiStore.map.leafletElement.closePopup()
    }
    if (typeof uiStore.closePoiTooltip === 'function') {
      uiStore.closePoiTooltip()
    }

    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(450)
    }
    const group = selectionStore.data.group
    const prevZoomToSelected = uiStore.zoomToSelected
    uiStore.zoomToSelected = false
    selectionStore.select(item, { data: { group } })
    // Keep map at user's dragged viewport when entering draw/create mode.
    setTimeout(() => {
      if (map && center && zoom != null) {
        map.setView(center, zoom, { animate: false })
      }
      uiStore.zoomToSelected = prevZoomToSelected
    }, 0)
  }

  return (
    <div className='add-item-pane'>
      <section>
        <div className='add-item'>
          <button
            className='add-item-button add-buffer-point' onClick={() => {
              selectNewItem(new PointBuffer())
            }}
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.add-buffer-point'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={bufferArountPoint} />
            </div>
          </button>
          {t('ITEM.POINT') && <span className='add-item-tooltip'>{t('ITEM.POINT')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button add-isochrone' onClick={() => {
              selectNewItem(new Isochrone())
            }}
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(uiStore.user.limits.max_regions, ' polygons.'),
                  selector: '.add-isochrone'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
              title={t('GLOBAL.TRAVEL_TIME')}
            >
              <SvgIcon html={travelTime} />
            </div>
          </button>
        </div>
        <div className='add-item'>
          <button
            className='add-item-button draw-shape' onClick={() => {
              selectNewItem(new Shape())
            }}
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.draw-shape'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={drawShape} />
            </div>
          </button>
          {t('ITEM.DRAW_SHAPE') && <span className='add-item-tooltip'>{t('ITEM.DRAW_SHAPE')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button add-line-buffer' onClick={() => {
              selectNewItem(new Line())
            }}
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.add-line-buffer'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={bufferArountLine} />
            </div>
          </button>
          {t('ITEM.LINE') && <span className='add-item-tooltip'>{t('ITEM.LINE')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button add-marker' onClick={() => {
              // save immediately and get added to general bucket
              const group = selectionStore.data.group
              const marker = new Marker()
              const grayColor = getDefaultGrayColor(dataStore && dataStore.markerColors)
              if (grayColor) marker.color_id = grayColor.id
              selectionStore.select(marker, { data: { group } })
            }}
            disabled={selectionStore.data.group.hasExceededMarkerLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededMarkerLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.add-marker'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={placeAMarker} />
            </div>
          </button>
          {t('ITEM.PLACE_MARKER') && <span className='add-item-tooltip'>{t('ITEM.PLACE_MARKER')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button add-boundary'
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
            onClick={() => {
              selectNewItem(new BoundaryList())
            }}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.add-boundary'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={placeAdministrativeBoundary} />
            </div>
          </button>
          {t('ITEM.PLACE_BOUNDARY') && <span className='add-item-tooltip'>{t('ITEM.PLACE_BOUNDARY')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button upload-shapefile'
            onClick={async () => {
              const map = uiStore && uiStore.map && uiStore.map.leafletElement
              const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
              const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
              if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
                uiStore.suppressMapBoundsTemporarily(450)
              }
              const prevZoomToSelected = uiStore && typeof uiStore.zoomToSelected !== 'undefined' ? uiStore.zoomToSelected : null
              if (uiStore && prevZoomToSelected !== null) {
                uiStore.zoomToSelected = false
              }
              uiStore.processing = true
              const list = new UserShapefileList()
              list.editMode = false
              await list.load()
              selectionStore.select(
                list,
                { data: { group: selectionStore.data.group } }
              )
              uiStore.processing = false
              setTimeout(() => {
                if (map && center && zoom != null) {
                  map.setView(center, zoom, { animate: false })
                }
                if (uiStore && prevZoomToSelected !== null) {
                  uiStore.zoomToSelected = prevZoomToSelected
                }
              }, 0)
            }}
            disabled={selectionStore.data.group.hasExceededRegionsLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededRegionsLimit &&
              (uiStore.openHoverTooltip({
                text: uiStore.limitErrorMessage(),
                selector: '.upload-shapefile'
              }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={uploadedShapeFiles} />
            </div>
          </button>
          {t('GLOBAL.UPLOADED_SHAPEFILES') && <span className='add-item-tooltip'>{t('GLOBAL.UPLOADED_SHAPEFILES')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button upload-markerfile'
            onClick={async () => {
              const map = uiStore && uiStore.map && uiStore.map.leafletElement
              const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
              const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
              if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
                uiStore.suppressMapBoundsTemporarily(450)
              }
              const prevZoomToSelected = uiStore && typeof uiStore.zoomToSelected !== 'undefined' ? uiStore.zoomToSelected : null
              if (uiStore && prevZoomToSelected !== null) {
                uiStore.zoomToSelected = false
              }
              uiStore.processing = true
              const list = new UserMarkerfileList()
              list.editMode = false
              await list.load()
              selectionStore.select(
                list,
                { data: { group: selectionStore.data.group } }
              )
              uiStore.processing = false
              setTimeout(() => {
                if (map && center && zoom != null) {
                  map.setView(center, zoom, { animate: false })
                }
                if (uiStore && prevZoomToSelected !== null) {
                  uiStore.zoomToSelected = prevZoomToSelected
                }
              }, 0)
            }}
            disabled={selectionStore.data.group.hasExceededMarkerLimit}
          >
            <div
              onMouseOver={() => {
                selectionStore.data.group.hasExceededMarkerLimit &&
                (uiStore.openHoverTooltip({
                  text: uiStore.limitErrorMessage(),
                  selector: '.upload-markerfile'
                }))
              }}
              onMouseLeave={() => uiStore.hideHoverTooltip()}
            >
              <SvgIcon html={uploadedMarkers} />
            </div>
          </button>
          {t('GLOBAL.UPLOADED_MARKERFILES') && <span className='add-item-tooltip'>{t('GLOBAL.UPLOADED_MARKERFILES')}</span>}
        </div>
        <div className='add-item'>
          <button
            className='add-item-button folder-actions-trigger'
            onClick={() => {
              const group = selectionStore.data.group
              if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
                uiStore.suppressMapBoundsTemporarily(450)
              }
              selectionStore.select(null, {
                type: 'FOLDER_ACTIONS',
                data: { group }
              })
            }}
          >
            <SvgIcon html={folderActions} />
          </button>
          <span className='add-item-tooltip'>{t('GLOBAL.FOLDER_ACTIONS')}</span>
        </div>
      </section>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'selectionStore', 'dataStore')(observer(AddItemPane)))
